import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, MailCheck, AlertCircle } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { translateError } from "../utils/i18nErrors";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VerifyEmailPage() {
  const { t } = useLanguage();
  const { user, updateUser } = useAuth();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [status, setStatus] = useState("verifying"); // verifying | success | error
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) { setStatus("error"); setError(t("emailVerify.invalidLink")); return; }
    axios.post(`${API}/auth/verify-email`, { token })
      .then(() => {
        setStatus("success");
        if (user) updateUser({ email_verified: true });
      })
      .catch((err) => {
        setStatus("error");
        setError(translateError(err.response?.data?.detail || "", t) || t("errors.unknownError"));
      });
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="verify-email-page" className="min-h-screen lg:min-h-[calc(100vh-5rem)] bg-gradient-to-br from-slate-50 to-white flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-slate-100 p-8 text-center">
        {status === "verifying" && (
          <>
            <Loader2 className="h-8 w-8 text-[#028CF8] animate-spin mx-auto mb-3" />
            <p className="text-sm text-slate-700" data-testid="verify-email-loading">{t("emailVerify.verifyingTitle")}</p>
          </>
        )}
        {status === "success" && (
          <>
            <div className="h-12 w-12 rounded-xl bg-green-50 flex items-center justify-center mb-3 mx-auto">
              <MailCheck className="h-6 w-6 text-green-600" />
            </div>
            <p className="font-bold text-lg text-slate-900 mb-1">{t("emailVerify.successTitle")}</p>
            <p className="text-sm text-slate-600 mb-5" data-testid="verify-email-success-message">{t("emailVerify.successBody")}</p>
            <button data-testid="verify-email-go-to-app" onClick={() => navigate(user ? (user.role === "employer" ? "/company/dashboard" : "/dashboard") : "/")}
              className="w-full h-11 btn-primary justify-center rounded-xl text-sm">
              {t("emailVerify.goToApp")}
            </button>
          </>
        )}
        {status === "error" && (
          <>
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2.5 mb-4" data-testid="verify-email-error">
              <AlertCircle className="h-4 w-4 flex-shrink-0" /> {error}
            </div>
            <Link to="/" data-testid="verify-email-back-home" className="text-xs text-[#028CF8] font-medium hover:underline">
              {t("emailVerify.goToApp")}
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
